/**
 * Admin i18n — restricted to FR + EN.
 *
 * The customer-facing @clari/web ships FR/EN/AR/ES, but the operator
 * console is internal and intentionally limited:
 *   - Operators work in the language of the operations team.
 *   - Limiting the locale set keeps audit-trail vocabulary consistent for
 *     compliance reviewers.
 *
 * Detection chain: ?lang= -> cookie -> localStorage -> navigator -> 'en'.
 * Unsupported requests (e.g. ?lang=ar) silently fall back to 'en'.
 */
import i18n from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { initReactI18next } from 'react-i18next'
import { NAMESPACES, getDir } from '@clari/i18n'

import enCommon from '@clari/i18n/locales/en/common.json'
import enAuth from '@clari/i18n/locales/en/auth.json'
import enErrors from '@clari/i18n/locales/en/errors.json'
import enDashboard from '@clari/i18n/locales/en/dashboard.json'

import frCommon from '@clari/i18n/locales/fr/common.json'
import frAuth from '@clari/i18n/locales/fr/auth.json'
import frErrors from '@clari/i18n/locales/fr/errors.json'
import frDashboard from '@clari/i18n/locales/fr/dashboard.json'

// Operator-only namespace, local to apps/admin (kept out of the shared 9-locale check).
import enAdmin from '@/locales/en'
import frAdmin from '@/locales/fr'

export const ADMIN_LOCALES = ['fr', 'en'] as const
export type AdminLocale = (typeof ADMIN_LOCALES)[number]
export const ADMIN_DEFAULT_LOCALE: AdminLocale = 'en'

export const ADMIN_NAMESPACES = [...NAMESPACES, 'admin'] as const

const resources = {
  en: { common: enCommon, auth: enAuth, errors: enErrors, dashboard: enDashboard, admin: enAdmin },
  fr: { common: frCommon, auth: frAuth, errors: frErrors, dashboard: frDashboard, admin: frAdmin },
} as const

export const PREFERRED_LANG_COOKIE = 'preferred_lang'

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: ADMIN_DEFAULT_LOCALE,
    supportedLngs: [...ADMIN_LOCALES],
    ns: [...ADMIN_NAMESPACES],
    defaultNS: 'common',
    interpolation: { escapeValue: false },
    detection: {
      order: ['querystring', 'cookie', 'localStorage', 'navigator'],
      lookupQuerystring: 'lang',
      lookupCookie: PREFERRED_LANG_COOKIE,
      lookupLocalStorage: 'i18nextLng',
      caches: ['cookie', 'localStorage'],
      cookieMinutes: 60 * 24 * 365,
      cookieOptions: { path: '/', sameSite: 'lax' },
    },
    returnNull: false,
  })

function isAdminLocale(value: string): value is AdminLocale {
  return (ADMIN_LOCALES as readonly string[]).includes(value)
}

function applyDirAttribute(locale: string): void {
  const safe: AdminLocale = isAdminLocale(locale) ? locale : ADMIN_DEFAULT_LOCALE
  document.documentElement.setAttribute('lang', safe)
  document.documentElement.setAttribute('dir', getDir(safe))
}

applyDirAttribute(i18n.language)
i18n.on('languageChanged', applyDirAttribute)

export { i18n }
